import { useMemo, useState, type FormEvent } from "react";
import { errorMessage, searchTools, setEnabledTools } from "../api";
import {
  accountTools,
  matchesKind,
  nextAllowList,
  type AccountTool,
  type ToolKind,
} from "../toolSelection";
import { enablementFor, toolsForConnection } from "../types";
import ToolKindFilter from "../components/ToolKindFilter";
import { useDashboard } from "./Dashboard";

/**
 * Every tool the user has, searchable in the words they would actually use.
 *
 * The other way to reach a tool is the dialog on its account's card, which works only if
 * you already know which account it is on. That is fine at one account and useless at five:
 * tool names are written for a model, so `trelloWriteCard` does not contain "task" and
 * `search_code` does not contain "find". The question a person has -- "what can read my
 * calendar?", "what can write anywhere?" -- shares no words with the thing they are
 * looking for, which is exactly the case substring search cannot serve.
 *
 * So typing filters by name as you go (instant, no request, and right when you *do* know
 * the name), and Enter sends the question to Gemini, which answers with tools rather than
 * matches. Both produce the same rows with the same switches, because the point of finding
 * a tool here is to switch it on.
 */
export default function Tools() {
  const { catalog, connections, server, project, toggleConnection, notify } =
    useDashboard();

  const [query, setQuery] = useState("");
  const [kind, setKind] = useState<ToolKind>("all");
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);

  /**
   * The last AI answer, or null when we are showing plain text matching. It holds ids and
   * reasons only -- the tools themselves come from `rows`, which the Firestore listeners
   * keep current, so toggling something does not make the result list go stale.
   */
  const [result, setResult] = useState<{
    query: string;
    summary: string;
    matches: { id: string; reason: string }[];
  } | null>(null);

  const rows = useMemo(
    () => accountTools(catalog, connections, server),
    [catalog, connections, server]
  );

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  /**
   * What the search leaves: the AI's ranking if there is one, otherwise a name filter.
   *
   * Kept apart from `shown` because the read/write filter narrows this further, and its
   * counts have to describe the list it is about to narrow -- "Write 1" next to four search
   * results is the useful reading; "Write 1" of every tool the user owns is noise.
   */
  const matched: { row: AccountTool; reason?: string }[] = useMemo(() => {
    if (result) {
      // Ranked, so the AI's order is kept. A tool can vanish between the search and now
      // (disconnected in another tab), which is why this filters rather than asserts.
      return result.matches.flatMap((m) => {
        const row = byId.get(m.id);
        return row ? [{ row, reason: m.reason }] : [];
      });
    }

    const needle = query.trim().toLowerCase();
    if (!needle) return rows.map((row) => ({ row }));

    return rows
      .filter((row) =>
        `${row.id} ${row.tool.title} ${row.tool.description} ${row.conn.accountLabel}`
          .toLowerCase()
          .includes(needle)
      )
      .map((row) => ({ row }));
  }, [result, byId, rows, query]);

  /** What is actually on screen, and therefore what the batch switches below act on. */
  const shown = useMemo(
    () => matched.filter(({ row }) => matchesKind(row.tool, kind)),
    [matched, kind]
  );

  const exposedCount = shown.filter(({ row }) => row.exposed).length;

  /** Whether the list on screen is a subset -- which is what "these" rather than "all" means. */
  const narrowed = Boolean(result) || query.trim() !== "" || kind !== "all";

  async function runSearch(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q || searching || !project) return;

    setSearching(true);
    try {
      const res = await searchTools({ projectId: project.id, query: q });
      setResult({ query: q, summary: res.data.summary, matches: res.data.matches });
    } catch (err) {
      // Includes "AI search is not configured on this deployment" -- in which case the
      // name filter on screen is already the answer, so nothing else has to change.
      notify(errorMessage(err), "error");
    } finally {
      setSearching(false);
    }
  }

  /**
   * Switch tools on or off, batched by account.
   *
   * The allow-list is one array per connection, so N tools on one account is one write and
   * not N -- and going one at a time would race: each call reads the list the last one
   * wrote, and the listener has not necessarily delivered it yet.
   */
  async function setExposed(targets: AccountTool[], on: boolean) {
    if (targets.length === 0 || busy) return;

    const byConnection = new Map<string, AccountTool[]>();
    for (const target of targets) {
      const group = byConnection.get(target.conn.id);
      if (group) group.push(target);
      else byConnection.set(target.conn.id, [target]);
    }

    setBusy(true);
    try {
      for (const group of byConnection.values()) {
        const { conn, provider, slug, accountEnabled } = group[0];

        // Exposing a tool on a switched-off account otherwise looks like it did nothing:
        // the allow-list would say yes and the account's own switch would still say no.
        // Announced rather than silent -- it is the one thing here that changes more than
        // the row that was clicked.
        if (on && !accountEnabled) {
          await toggleConnection(slug, true);
          notify(`Switched ${conn.accountLabel} on so its tools can be exposed.`);
        }

        const available = toolsForConnection(provider, conn);
        const allowList = enablementFor(conn, server).allowList;
        const names = group.map((t) => t.tool.name);

        await setEnabledTools({
          connectionId: conn.id,
          tools: nextAllowList(available, allowList, names, on),
        });
      }
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h2>Tools</h2>
          <p className="section-sub">
            Every tool across your connected accounts, in one list. Type to filter by name,
            or ask in plain language — “what can read my calendar?” — and the search finds
            tools by what they do. Switching one on or off takes effect immediately; your
            MCP URL never changes.
          </p>
        </div>
      </div>

      <form className="search" onSubmit={runSearch}>
        <input
          className="search-input"
          value={query}
          placeholder="Search tools, or describe what you need…"
          aria-label="Search tools"
          spellCheck={false}
          autoComplete="off"
          disabled={rows.length === 0}
          onChange={(e) => {
            setQuery(e.target.value);
            // Any edit puts the list back under the user's control. Leaving the previous
            // answer up while the box says something else is the page lying about what
            // it is showing.
            setResult(null);
          }}
        />
        <button className="btn" type="submit" disabled={!query.trim() || searching}>
          {searching ? "Searching…" : "Ask AI"}
        </button>
      </form>

      {rows.length === 0 ? (
        <div className="empty">
          <p>No tools to search yet.</p>
          <p className="empty-sub">
            Connect an account on the Providers page and its tools show up here.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar">
            <span className="search-count">
              {result ? (
                <>
                  {shown.length} result{shown.length === 1 ? "" : "s"} for “{result.query}”
                  {" · "}
                  <button
                    className="link-btn"
                    onClick={() => {
                      // All three, because that is what the label promises. Dropping the
                      // answer alone left the question in the box, so the name filter took
                      // over and "show all 16" showed none of them -- a plain-language
                      // question shares no words with a tool name, which is the whole
                      // reason the AI path exists.
                      setResult(null);
                      setQuery("");
                      setKind("all");
                    }}
                  >
                    show all {rows.length}
                  </button>
                </>
              ) : (
                <>
                  {shown.length} of {rows.length} tool{rows.length === 1 ? "" : "s"}
                  {" · "}
                  {exposedCount} exposed
                </>
              )}
            </span>

            {/* Counted over the search results rather than every tool: the question this
                answers is "how much of what I just found can write?". */}
            <ToolKindFilter
              tools={matched.map(({ row }) => row.tool)}
              value={kind}
              onChange={setKind}
            />

            {/* Batch switches for exactly what is on screen -- always rendered, so the
                bar does not reshuffle when a filter empties it. The point of a search that
                understands "everything that can write" is being able to act on the answer
                in one move rather than clicking down the list. */}
            <span className="search-actions">
              <button
                className="btn btn-subtle btn-sm"
                disabled={busy || shown.length === 0 || exposedCount === shown.length}
                onClick={() => setExposed(shown.map((s) => s.row), true)}
              >
                Expose {narrowed ? "these" : "all"}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                disabled={busy || exposedCount === 0}
                onClick={() => setExposed(shown.map((s) => s.row), false)}
              >
                Hide {narrowed ? "these" : "all"}
              </button>
            </span>
          </div>

          {result?.summary && <p className="search-summary">{result.summary}</p>}

          {shown.length === 0 ? (
            <div className="empty">
              {/* The read/write filter emptying a list that did match is a different
                  answer from nothing matching, and a useful one -- it says every tool
                  here is of the other kind. Advising a better search would be wrong. */}
              {matched.length > 0 ? (
                <>
                  <p>
                    {kind === "read"
                      ? "Nothing here is read-only."
                      : "Nothing here can write."}
                  </p>
                  <p className="empty-sub">
                    All {matched.length} tool{matched.length === 1 ? "" : "s"} here{" "}
                    {kind === "read" ? "can change something" : "only read"}.
                  </p>
                </>
              ) : (
                <>
                  <p>Nothing matched.</p>
                  <p className="empty-sub">
                    {result
                      ? "Try describing the job rather than the tool — “file an issue”, “post to a channel”."
                      : "Nothing has that in its name. Press Ask AI to search by what tools do instead."}
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="tools tools-page">
              {shown.map(({ row, reason }) => (
                <label key={row.id} className="tool">
                  <input
                    type="checkbox"
                    checked={row.exposed}
                    disabled={busy}
                    onChange={(e) => setExposed([row], e.target.checked)}
                  />
                  <span className="tool-body">
                    <span className="tool-name">
                      <code>{row.id}</code>
                      {row.tool.readOnly && <span className="tag">read-only</span>}
                      {/* The switch is not the whole story on an account that is off:
                          the tool is allowed and still not being served. */}
                      {!row.accountEnabled && <span className="tag">account off</span>}
                      <span className="tool-account">
                        {row.provider.icon} {row.conn.accountLabel}
                      </span>
                    </span>
                    <span className="tool-desc">{row.tool.description}</span>
                    {reason && <span className="tool-reason">{reason}</span>}
                  </span>
                </label>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
